import { Route as FoundRoute, type RouteConfig } from 'found';
import React, { useEffect } from 'react';

// The slice of found's RenderProps this render actually reads — a structural
// supertype of found's RouteRenderArgs, so the class stays assignable to
// found's RouteConfig. found spreads the whole match into props at runtime.
interface RouteRenderProps {
	routes?: RouteConfig[];
}

interface RouteRenderArgs {
	Component?: React.ComponentType<RouteRenderProps> | null;
	props?: RouteRenderProps | null;
}

// Sets the title from a commit effect, not during element creation: found
// keeps resolving a match's pending getComponent promises even after the
// router has navigated away (e.g. a guard redirect), and it calls render for
// that stale resolution too — but React never commits those elements, so an
// effect can't overwrite the new page's title the way a render-time side
// effect did.
const RouteDocumentTitle: React.FC<{ title: string; children?: React.ReactNode }> = ({
	title,
	children,
}) => {
	useEffect(() => {
		document.title = title;
	}, [title]);
	return <>{children}</>;
};

export default class Route extends FoundRoute {
	public render(routeProps?: RouteRenderArgs) {
		if (!routeProps) {
			return null;
		}
		const { Component, props } = routeProps;

		// null (not undefined) means "still resolving" — found passes
		// Component: null while getComponent is pending and props: null while
		// getData is pending. Returning undefined makes the resolver yield
		// undefined elements, and found's StaticContainer then keeps the
		// previous page rendered until this match resolves, instead of
		// unmounting the whole tree into a blank frame mid-navigation.
		if (Component === null || props === null) {
			return undefined;
		}

		if (Component && props) {
			// Route props are Object.assign'ed onto the instance by found's route
			// class, so `title` exists at runtime but not in the typings.
			const { title } = this as unknown as { title?: string };
			// Only the deepest titled route of the match sets the title (effects
			// run child-first, so without this gate a titled ancestor would win).
			const deepestTitled = props.routes
				? [...props.routes].reverse().find((route) => route.title)
				: this;

			if (title && deepestTitled === this) {
				return (
					<RouteDocumentTitle title={title}>
						<Component {...props} />
					</RouteDocumentTitle>
				);
			}

			return <Component {...props} />;
		}
		return null;
	}
}
